import React, { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import UserSessionStore, { isGuestUserSession, useUserSessionContext } from 'features/session/userSessionStore';
import { useLogoutBridge, triggerLogoutOnBeforeUnload } from 'features/session/logoutBridge';
import { useHostAuth, isEmbedded } from 'features/session/VirtualAuthContext';
import { LoginModal } from 'features/session/LoginModal';
import { registerOpenLogin } from 'mfe/auth-bridge';
import { getActiveVirtualSessionId } from 'shared/api/http-client';
import Emitter from 'shared/events/emitter';
import AppRoutes from 'app/AppRoutes';
import { useBootstrapSession } from 'features/session/useBootstrapSession';
import { ticketsQueryKeys } from 'features/tickets/queries';
import { notifyHostBalanceChanged } from 'features/session/hostBalanceBridge';

import 'styles/base.scss';

function SessionBootstrapper() {
  useBootstrapSession();
  return null;
}

function WinBalanceBridge() {
  const [{ user }, dispatch] = useUserSessionContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handler = (payload: { new_balance: number }) => {
      if (isGuestUserSession(user)) return;
      const updated = { ...user, player_balance: payload.new_balance };
      dispatch({ type: 'SET_USER', payload: updated });
      notifyHostBalanceChanged(payload.new_balance);
      queryClient.invalidateQueries({ queryKey: ticketsQueryKeys.creditAvailable });
    };
    Emitter.on('VS.USER_WIN_BALANCE', handler);
    return () => { Emitter.off('VS.USER_WIN_BALANCE', handler); };
  }, [dispatch, queryClient, user]);

  return null;
}

function LoginModalHost() {
  const hostAuth = useHostAuth();
  const embedded = isEmbedded(hostAuth);
  const [loginOpen, setLoginOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleOpen = useCallback(() => {
    if (embedded && hostAuth.onAuthRequired) {
      hostAuth.onAuthRequired();
    } else {
      setLoginOpen(true);
    }
  }, [embedded, hostAuth]);

  useEffect(() => {
    registerOpenLogin(handleOpen);
    return () => registerOpenLogin(null);
  }, [handleOpen]);

  const handleLoginSuccess = useCallback(() => {
    setLoginOpen(false);
    queryClient.invalidateQueries({ queryKey: ['session', 'bootstrap'] });
  }, [queryClient]);

  if (embedded) return null;

  return (
    <LoginModal
      open={loginOpen}
      onClose={() => setLoginOpen(false)}
      onSuccess={handleLoginSuccess}
    />
  );
}

function App() {
  useLogoutBridge();

  useEffect(() => {
    const onBeforeUnload = () => {
      if (getActiveVirtualSessionId()) {
        triggerLogoutOnBeforeUnload();
      }
      return false;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return (
    <UserSessionStore>
      <SessionBootstrapper />
      <LoginModalHost />
      <WinBalanceBridge />
      <AppRoutes />
    </UserSessionStore>
  );
}

export default App;
